import React, { useCallback, useEffect, useRef, useState } from 'react';
import ContextMenu from '../Overlays/ContextMenu';
import InlineEditor from '../ProjectPanel/InlineEditor';
import Modal from '../Overlays/Modal';
import { listChatsForProject, renameChat, deleteChat, printChat } from '../../helpers/chatHelpers';
import { computeOpenBehavior } from '../../helpers/navHelpers';
import { docMapKey } from '../../helpers/mapKey';
import { getPreference } from '../../helpers/preferencesHelpers';
import { getDefaultRenameMode, getRenameModePreferenceKey, isAutoRenameEligibleName, normalizeRenameMode } from '../../helpers/renameHelpers';
import Icon from '../Icon';

export default function ChatsPanel({
  fixed,
  className,
  projectId,
  chatsPanelOpen,
  refreshToken = 0,
  onOpenChat,
  onCreateChat,
  projectReadOnly = false,
  tabs = [],
  setTabs,
  setActiveTabId,
  dragStart,
  dragEnd,
  activePane = 'left',
  leftActiveTabId = null,
  rightActiveTabId = null,
  user = null,
  itemsMap,
  setItemsMap
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chats, setChats] = useState([]);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [chatRenameMode, setChatRenameMode] = useState(() => {
    try {
      return normalizeRenameMode(getPreference(null, getRenameModePreferenceKey('chat'), getDefaultRenameMode('chat')), 'chat');
    } catch (err) {
      return getDefaultRenameMode('chat');
    }
  });
  const menuRefs = useRef({});

  const loadChats = useCallback(async () => {
    if (!chatsPanelOpen || !projectId) {
      setChats([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const nextChats = await listChatsForProject(projectId);
      setChats(Array.isArray(nextChats) ? nextChats : []);
    } catch (err) {
      setError(err?.message || 'Failed to load chats.');
    } finally {
      setLoading(false);
    }
  }, [chatsPanelOpen, projectId]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      await loadChats();
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [loadChats, refreshToken]);

  useEffect(() => {
    const handleRenameModeChanged = (event) => {
      if (String(event?.detail?.docType || '').trim().toLowerCase() !== 'chat') return;
      setChatRenameMode(normalizeRenameMode(event?.detail?.mode, 'chat'));
    };

    window.addEventListener('rename-mode-changed', handleRenameModeChanged);
    return () => window.removeEventListener('rename-mode-changed', handleRenameModeChanged);
  }, []);

  useEffect(() => {
    const handleDocRenamed = (event) => {
      const renamedDocId = String(event?.detail?.docId || '').trim();
      const nextName = String(event?.detail?.name || '').trim();
      const nextProjectId = String(event?.detail?.projectId || '').trim();
      const renamedDocType = String(event?.detail?.docType || '').trim().toLowerCase();
      if (!renamedDocId || !nextName || renamedDocType !== 'chat') return;
      if (nextProjectId && String(projectId || '').trim() && nextProjectId !== String(projectId).trim()) return;

      setChats((prev) => (Array.isArray(prev)
        ? prev.map((chat) => (String(chat?.id || '') === renamedDocId ? { ...chat, name: nextName } : chat))
        : prev));
    };

    window.addEventListener('doc-renamed', handleDocRenamed);
    return () => window.removeEventListener('doc-renamed', handleDocRenamed);
  }, [projectId]);

  const focusedTabId = activePane === 'right' ? rightActiveTabId : leftActiveTabId;

  const handleRenameChat = async (chat) => {
    const nextName = editName.trim();
    if (!nextName) {
      setEditingId(null);
      return;
    }

    await renameChat({
      chat,
      nextName,
      itemsMap,
      setItemsMap,
      tabs,
      setTabs,
      projectId,
      userId: user?.id
    });
    setEditingId(null);
    setEditName('');
    await loadChats();
  };

  const handleDeleteChat = async () => {
    if (!pendingDelete || !projectId) return;

    await deleteChat({
      chat: pendingDelete,
      projectId,
      userId: user?.id,
      itemsMap,
      setItemsMap,
      tabs,
      setTabs,
      setActiveTabId
    });

    setPendingDelete(null);
    await loadChats();
  };

  const handlePrint = (chat) => {
    printChat(chat, onOpenChat);
  };

  return (
    <div
      id="chatsPanel"
      className={`${fixed ? 'fixed' : 'overlay'} ${className || ''}`}
    >
      <div id="chatsHeader">
        <div className="projectName">
          <h3>Chats</h3>
        </div>

        <div className="projectActions">
          <button
            className="iconTextButton list"
            onClick={() => onCreateChat?.()}
            disabled={projectReadOnly || !projectId}
          >
            <Icon icon={'chat-ghost-add'} className="icon chat-ghost-add" />
            <span>Start a Chat</span>
          </button>
        </div>
      </div>

      <div id="chatsContent">
        <div className="projectListHeader">
          <span className="projectListTitle">All chats</span>
        </div>

        {loading && <div className="projectListEmpty">Loading chats...</div>}
        {!loading && error && <div className="projectListEmpty">{error}</div>}
        {!loading && !error && chats.length === 0 && (
          <div className="projectListEmpty">No chats yet.</div>
        )}

        {!loading && !error && chats.length > 0 && (
          <ul className="projectList chatsList">
            {chats.map(chat => {
              const latestChat = itemsMap instanceof Map ? itemsMap.get(docMapKey(chat.id)) : itemsMap?.[docMapKey(chat.id)];
              const resolvedChat = latestChat ? { ...chat, ...latestChat } : chat;
              const chatId = String(resolvedChat?.id || '');
              const menuId = docMapKey(resolvedChat.id);
              if (!menuRefs.current[menuId]) {
                menuRefs.current[menuId] = React.createRef();
              }

              const openTab = tabs.find(t => String(t.docId) === chatId);
              const isActive = Boolean(openTab && (openTab.id === leftActiveTabId || openTab.id === rightActiveTabId));
              const isOpen = Boolean(openTab);
              const isFocused = Boolean(openTab && openTab.id === focusedTabId);
              const isEditing = editingId === menuId;
              const canRename = !projectReadOnly;
              const showUntitledAutoStyle = isAutoRenameEligibleName(resolvedChat?.name || '', 'chat', chatRenameMode);
              return (
                <li key={resolvedChat.id}>
                  <div
                    className={`projectItem chatItem ${isActive ? 'active' : ''} ${isOpen && !isActive ? 'open' : ''} ${isFocused ? 'focused' : ''}`.trim()}
                    id={`item-${resolvedChat.id}`}
                    draggable={!isEditing}
                    onDragStart={(e) => {
                      if (isEditing) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      e.stopPropagation();
                      dragStart?.(e, resolvedChat.id, 'doc', {
                        name: resolvedChat?.name || 'Untitled Chat',
                        docType: 'chat',
                        readOnly: true
                      });
                    }}
                    onDragEnd={() => {
                      dragEnd?.();
                    }}
                    onClick={() => {
                      try {
                        const beh = computeOpenBehavior({ type: 'chat', tabs, activePane, leftActiveTabId, rightActiveTabId });
                        const mode = beh?.mode || 'replace';
                        const preferredPane = beh?.preferredPane || activePane;
                        onOpenChat?.(resolvedChat, mode, preferredPane);
                      } catch (err) {
                        onOpenChat?.(resolvedChat, 'replace');
                      }
                    }}
                    onDoubleClick={() => {
                      if (!canRename) return;
                      setEditingId(menuId);
                      setEditName(resolvedChat?.name || 'Untitled Chat');
                    }}
                  >
                    <Icon icon={'chat-ghost'} className="icon chat-ghost" />
                    {isEditing ? (
                      <InlineEditor
                        value={editName}
                        onChange={setEditName}
                        onSave={() => handleRenameChat(resolvedChat)}
                        onCancel={() => setEditingId(null)}
                      />
                    ) : (
                      <span className={showUntitledAutoStyle ? 'untitledAutoName' : ''}>{resolvedChat?.name || 'Untitled Chat'}</span>
                    )}

                    <button
                      ref={menuRefs.current[menuId]}
                      className="iconButton"
                      data-tooltip-block-parent="true"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === menuId ? null : menuId);
                      }}
                      aria-label="Chat actions"
                    >
                      <Icon icon={'dots-three-vertical'} className="icon dots-three-vertical" />
                    </button>

                    {menuOpenId === menuId && (
                      <ContextMenu
                        triggerRef={menuRefs.current[menuId]}
                        onClose={() => setMenuOpenId(null)}
                        items={[
                          {
                            label: 'Open in new tab',
                            iconClass: 'arrow-square-out',
                            onClick: () => {
                              setMenuOpenId(null);
                              onOpenChat?.(resolvedChat, 'new');
                            }
                          },
                          !projectReadOnly && {
                            label: 'Rename',
                            iconClass: 'pencil-simple',
                            onClick: () => {
                              setMenuOpenId(null);
                              setEditingId(menuId);
                              setEditName(resolvedChat?.name || 'Untitled Chat');
                            }
                          },
                          {
                            label: 'Print',
                            iconClass: 'printer',
                            onClick: () => {
                              setMenuOpenId(null);
                              handlePrint(resolvedChat);
                            }
                          },
                          !projectReadOnly && { divider: true },
                          !projectReadOnly && {
                            label: 'Delete',
                            iconClass: 'trash',
                            onClick: () => {
                              setMenuOpenId(null);
                              setPendingDelete(resolvedChat);
                            }
                          }
                        ].filter(Boolean)}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Modal
        isOpen={!!pendingDelete}
        title="Confirm Delete"
        message={pendingDelete ? `Are you sure you want to delete "${pendingDelete.name}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteChat}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

